/**
 * Tela de Contratos: o contrato de matricula do aluno, do modelo no Google
 * Docs ate a assinatura na Autentique.
 *
 * Aba Contratos: status em selo e as acoes de gerar, enviar, baixar o PDF e
 * anexar um contrato assinado fora do CRM. Aba Modelos: o Google Doc de cada
 * modelo e os marcadores que ele aceita. O PDF e o envio saem em segundo
 * plano (job); enquanto algum esta em andamento a lista se atualiza sozinha.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '../../layout/Shell.tsx';
import { useSessao } from '../../sessao.tsx';
import { ErroApi, escrever, ler, mensagemDe, type FalhaApi } from '../../ui/api.ts';
import {
  Aviso, Barra, BlocoInicio, Botao, Busca, Campo, Formulario, Painel, Selo, Tabela, type Coluna, type Tom,
} from '../../ui/componentes.tsx';
import { data, dataHora, hoje, relativo } from '../../ui/formatos.ts';
import type { TelaDeModulo } from '../contrato.ts';
import './contratos.css';

type Signatario = {
  nome: string; email: string; link: string | null;
  assinado_em: string | null; recusado_em: string | null;
};

type Contrato = {
  id: string;
  pessoa_id: string;
  pessoa_nome: string;
  pessoa_email: string | null;
  matricula_id: string | null;
  origem: 'gerado' | 'externo';
  status: 'rascunho' | 'gerando' | 'gerado' | 'enviado' | 'assinado_parcial' | 'assinado'
    | 'recusado' | 'cancelado' | 'erro';
  status_rotulo: string;
  aguardando_conector: boolean;
  enviando: boolean;
  modelo_nome: string | null;
  curso: string | null;
  turma: string | null;
  valor_formatado: string | null;
  sandbox: boolean;
  link_assinatura: string | null;
  signatarios: Signatario[];
  pdf_url: string | null;
  pdf_assinado_url: string | null;
  gerado_em: string | null;
  enviado_em: string | null;
  assinado_em: string | null;
  erro: string | null;
  criado_em: string;
  atualizado_em: string;
};

type Modelo = {
  id: string; nome: string; google_doc_id: string; google_doc_url: string;
  curso_codigo: string | null; ativo: boolean; criado_em: string; atualizado_em: string;
};

type Marcador = { marcador: string; descricao: string; obrigatorio: boolean };

type Pendente = {
  matricula_id: string; pessoa_id: string; pessoa_nome: string; ficha_status: string;
  ficha_faltando: string[]; curso_nome: string; turma_nome: string; valor_formatado: string;
};

type PessoaBusca = { id: string; nome: string; telefone_formatado: string | null; email: string | null };

const TOM: Record<Contrato['status'], Tom> = {
  rascunho: 'neutro', gerando: 'info', gerado: 'marca', enviado: 'info',
  assinado_parcial: 'aviso', assinado: 'ok', recusado: 'erro', cancelado: 'neutro', erro: 'erro',
};

const FILTROS: readonly { valor: string; rotulo: string }[] = [
  { valor: '', rotulo: 'Todos os status' },
  { valor: 'gerado', rotulo: 'Gerado' },
  { valor: 'enviado', rotulo: 'Enviado' },
  { valor: 'assinado_parcial', rotulo: 'Assinado em parte' },
  { valor: 'assinado', rotulo: 'Assinado' },
  { valor: 'recusado', rotulo: 'Recusado' },
  { valor: 'erro', rotulo: 'Erro' },
  { valor: 'cancelado', rotulo: 'Cancelado' },
];

const ROTULO_CAMPO: Record<string, string> = {
  nome: 'nome', cpf: 'CPF', telefone: 'telefone', email: 'e-mail', cep: 'CEP',
  logradouro: 'rua', numero: 'número', bairro: 'bairro', cidade: 'cidade', uf: 'UF',
};

const LIMITE_PDF = 10 * 1024 * 1024;

function usePermissao(codigo: string): boolean {
  const { estado } = useSessao();
  return estado.fase === 'dentro' && estado.eu.permissoes.includes(codigo);
}

function SeloContrato({ c }: { c: Contrato }) {
  const tom: Tom = c.aguardando_conector ? 'aviso' : c.enviando ? 'info' : TOM[c.status];
  return (
    <span className="contratos-selos">
      <Selo tom={tom}>{c.status_rotulo}</Selo>
      {c.sandbox && <Selo tom="neutro">Teste</Selo>}
      {c.origem === 'externo' && <Selo tom="neutro">Fora do CRM</Selo>}
    </span>
  );
}

/** Link com cara de botao: o PDF baixa pela sessao, sem passar pelo React. */
function BotaoLink({ href, children }: { href: string; children: string }) {
  return (
    <a className="ui-botao ui-botao--secundario contratos-link-botao" href={href} download>
      {children}
    </a>
  );
}

const andamento = (c: Contrato) => c.status === 'gerando' || c.enviando;

// Detalhe do contrato

function PainelContrato({ contrato, aoFechar }: { contrato: Contrato; aoFechar: () => void }) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<Error | null>(null);
  const podeEnviar = usePermissao('contratos.enviar');
  const podeGerar = usePermissao('contratos.gerar');
  const podeCancelar = usePermissao('contratos.excluir');
  const atualizar = () => qc.invalidateQueries({ queryKey: ['contratos'] });

  const acao = (tool: string, entrada: Record<string, unknown>) => ({
    mutationFn: () => escrever<Contrato>(tool, entrada),
    onSuccess: async () => { setErro(null); await atualizar(); },
    onError: (e: unknown) => setErro(e as Error),
  });
  const enviar = useMutation(acao('contratos.enviar', { id: contrato.id }));
  const gerarDeNovo = useMutation({
    ...acao('contratos.gerar', { matricula_id: contrato.matricula_id }),
    onSuccess: async () => { await atualizar(); aoFechar(); },
  });
  const cancelar = useMutation(acao('contratos.cancelar', { id: contrato.id }));

  const c = contrato;
  const cancelavel = ['rascunho', 'gerando', 'gerado', 'enviado', 'assinado_parcial', 'erro']
    .includes(c.status);
  const refazer = c.matricula_id !== null && ['erro', 'cancelado', 'recusado'].includes(c.status);

  return (
    <Painel aberto aoFechar={aoFechar} titulo={c.pessoa_nome}
      subtitulo={c.origem === 'externo' ? 'Assinado fora do CRM' : `Criado ${relativo(c.criado_em)}`}
      rodape={(
        <>
          {podeCancelar && cancelavel && (
            <Botao variante="perigo" desativado={cancelar.isPending}
              aoClicar={() => { if (window.confirm('Cancelar este contrato?')) cancelar.mutate(); }}>
              Cancelar contrato
            </Botao>
          )}
          {c.pdf_url !== null && <BotaoLink href={c.pdf_url}>Baixar PDF</BotaoLink>}
          {c.pdf_assinado_url !== null && (
            <BotaoLink href={c.pdf_assinado_url}>Baixar assinado</BotaoLink>
          )}
          {podeGerar && refazer && (
            <Botao variante="primario" desativado={gerarDeNovo.isPending}
              aoClicar={() => gerarDeNovo.mutate()}>Gerar de novo</Botao>
          )}
          {podeEnviar && c.status === 'gerado' && (
            <Botao variante="primario" desativado={enviar.isPending || c.enviando}
              aoClicar={() => enviar.mutate()}>
              {c.enviando ? 'Enviando' : c.aguardando_conector ? 'Enviar de novo' : 'Enviar para assinatura'}
            </Botao>
          )}
        </>
      )}
    >
      <SeloContrato c={c} />
      {c.status === 'gerando' && <Aviso tom="info">O PDF está sendo gerado a partir do modelo.</Aviso>}
      {c.aguardando_conector && (
        <Aviso tom="aviso">
          A Autentique ainda não está conectada. Configure o conector em Integrações e clique em
          Enviar de novo.
        </Aviso>
      )}
      {c.erro !== null && <Aviso tom="erro">{c.erro}</Aviso>}
      {c.sandbox && (
        <Aviso tom="neutro">Enviado no modo de teste da Autentique: não vale como assinatura.</Aviso>
      )}
      {erro !== null && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}

      <dl className="ui-dados">
        <div><dt>Aluno</dt>
          <dd><Link to={`/pessoas?id=${c.pessoa_id}`}>Abrir a ficha de {c.pessoa_nome}</Link></dd>
        </div>
        <div><dt>Curso</dt><dd>{c.curso ?? 'Não informado'}</dd></div>
        <div><dt>Turma</dt><dd>{c.turma ?? 'Não informada'}</dd></div>
        <div><dt>Valor</dt><dd>{c.valor_formatado ?? 'Não informado'}</dd></div>
        <div><dt>Modelo</dt><dd>{c.modelo_nome ?? (c.origem === 'externo' ? 'Não se aplica' : '')}</dd></div>
        {c.gerado_em !== null && <div><dt>Gerado em</dt><dd>{dataHora(c.gerado_em)}</dd></div>}
        {c.enviado_em !== null && <div><dt>Enviado em</dt><dd>{dataHora(c.enviado_em)}</dd></div>}
        {c.assinado_em !== null && (
          <div><dt>Assinado em</dt>
            <dd>{c.origem === 'externo' ? data(c.assinado_em) : dataHora(c.assinado_em)}</dd></div>
        )}
        {c.link_assinatura !== null && (
          <div><dt>Link de assinatura</dt>
            <dd><a href={c.link_assinatura} target="_blank" rel="noreferrer">{c.link_assinatura}</a></dd>
          </div>
        )}
      </dl>

      {c.signatarios.length > 0 && (
        <>
          <h3 className="contratos-secao-titulo">Quem assina</h3>
          <ul className="contratos-lista">
            {c.signatarios.map((s) => (
              <li key={s.email} className="contratos-opcao">
                <span className="contratos-opcao__texto">
                  <span className="contratos-opcao__nome">{s.nome || s.email}</span>
                  <span className="contratos-opcao__detalhe">{s.email}</span>
                </span>
                {s.recusado_em !== null ? <Selo tom="erro">Recusou</Selo>
                  : s.assinado_em !== null ? <Selo tom="ok">Assinou</Selo>
                    : <Selo tom="neutro">Pendente</Selo>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Painel>
  );
}

// Gerar a partir da matricula

function PainelGerar({ aoFechar }: { aoFechar: () => void }) {
  const qc = useQueryClient();
  const [termo, setTermo] = useState('');
  const [modeloId, setModeloId] = useState('');
  const [erro, setErro] = useState<Error | null>(null);
  const pendentes = useQuery({
    queryKey: ['contratos', 'pendentes', termo],
    queryFn: () => ler<{ itens: Pendente[] }>('contratos.matriculas_pendentes', { termo, limite: 100 }),
  });
  const modelos = useQuery({
    queryKey: ['contratos', 'modelos'],
    queryFn: () => ler<{ itens: Modelo[] }>('contratos.modelos_listar'),
  });
  const gerar = useMutation({
    mutationFn: (matriculaId: string) => escrever<Contrato>('contratos.gerar', {
      matricula_id: matriculaId, ...(modeloId !== '' ? { modelo_id: modeloId } : {}) }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['contratos'] }); aoFechar(); },
    onError: (e) => setErro(e as Error),
  });
  const ativos = (modelos.data?.itens ?? []).filter((m) => m.ativo);

  return (
    <Painel aberto aoFechar={aoFechar} titulo="Gerar contrato"
      subtitulo="Escolha a matrícula. Só ficha confirmada e completa gera contrato.">
      {erro !== null && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}
      {modelos.isSuccess && ativos.length === 0 && (
        <Aviso tom="aviso">Nenhum modelo ativo. Cadastre o Google Doc na aba Modelos.</Aviso>
      )}
      <Formulario aoEnviar={() => {}}>
        <Campo rotulo="Modelo" dica="Sem escolher, vale o modelo do curso ou o geral.">
          {(id) => (
            <select id={id} value={modeloId} onChange={(e) => setModeloId(e.target.value)}>
              <option value="">Modelo do curso (automático)</option>
              {ativos.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
          )}
        </Campo>
      </Formulario>
      <Busca valor={termo} aoMudar={setTermo} dica="Buscar aluno pelo nome" />
      {pendentes.isPending && <EstadoCarregando texto="Carregando matrículas" />}
      {pendentes.isError && (
        <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(pendentes.error)} />
      )}
      {pendentes.isSuccess && pendentes.data.itens.length === 0 && (
        <EstadoVazio titulo="Nenhuma matrícula esperando contrato"
          texto="Toda matrícula já tem contrato em andamento ou assinado." />
      )}
      {pendentes.isSuccess && pendentes.data.itens.length > 0 && (
        <ul className="contratos-lista">
          {pendentes.data.itens.map((m) => {
            const pronta = m.ficha_status === 'confirmada' && m.ficha_faltando.length === 0;
            return (
              <li key={m.matricula_id} className="contratos-opcao">
                <span className="contratos-opcao__texto">
                  <span className="contratos-opcao__nome">{m.pessoa_nome}</span>
                  <span className="contratos-opcao__detalhe">
                    {m.curso_nome}, {m.turma_nome}, {m.valor_formatado}
                  </span>
                  {!pronta && (
                    <span className="contratos-opcao__detalhe">
                      {m.ficha_status !== 'confirmada'
                        ? 'Ficha ainda não confirmada em Pessoas.'
                        : `Falta na ficha: ${m.ficha_faltando.map((f) => ROTULO_CAMPO[f] ?? f).join(', ')}.`}
                    </span>
                  )}
                </span>
                <Botao variante={pronta ? 'primario' : 'secundario'}
                  desativado={!pronta || gerar.isPending}
                  {...(!pronta ? { titulo: 'Confirme a ficha em Pessoas antes de gerar' } : {})}
                  aoClicar={() => gerar.mutate(m.matricula_id)}>Gerar</Botao>
              </li>
            );
          })}
        </ul>
      )}
    </Painel>
  );
}

// Anexar contrato assinado fora do CRM

function lerComoBase64(arquivo: File): Promise<string> {
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result).replace(/^data:[^,]*;base64,/, ''));
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/** A rota de anexar da tela aceita PDF maior que a rota gerada da tool. */
async function enviarAnexo(entrada: Record<string, unknown>): Promise<Contrato> {
  const r = await fetch('/api/v1/contratos/arquivo/anexar_assinado', {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entrada),
  });
  const corpo = await r.json().catch(() => ({})) as {
    code?: string; message?: string; details?: { campo?: string | null; erro?: string }[];
  } & Contrato;
  if (!r.ok) {
    const falhas: FalhaApi[] = (corpo.details ?? []).map((d) => ({
      codigo: 'erro', mensagem: d.erro ?? '', ...(d.campo ? { campo: d.campo } : {}) }));
    throw new ErroApi(r.status, corpo.code ?? 'erro',
      corpo.message ?? (r.status === 413 ? 'O arquivo é grande demais.' : 'Não foi possível anexar.'),
      falhas);
  }
  return corpo;
}

function PainelAnexar({ aoFechar }: { aoFechar: () => void }) {
  const qc = useQueryClient();
  const [termo, setTermo] = useState('');
  const [pessoa, setPessoa] = useState<PessoaBusca | null>(null);
  const [matriculaId, setMatriculaId] = useState('');
  const [assinadoEm, setAssinadoEm] = useState(hoje());
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<Error | null>(null);

  const pessoas = useQuery({
    queryKey: ['contratos', 'pessoas', termo],
    queryFn: () => ler<{ itens: PessoaBusca[] }>('pessoas.buscar', { termo, limite: 8 }),
    enabled: pessoa === null && termo.trim().length >= 2,
  });
  const pendentes = useQuery({
    queryKey: ['contratos', 'pendentes', ''],
    queryFn: () => ler<{ itens: Pendente[] }>('contratos.matriculas_pendentes', { limite: 200 }),
    enabled: pessoa !== null,
  });
  const daPessoa = (pendentes.data?.itens ?? []).filter((m) => m.pessoa_id === pessoa?.id);

  const anexar = useMutation({
    mutationFn: async () => {
      if (pessoa === null) throw new ErroApi(400, 'pessoa', 'Escolha o aluno.', []);
      if (arquivo === null) throw new ErroApi(400, 'arquivo', 'Escolha o PDF assinado.', []);
      if (arquivo.size > LIMITE_PDF) throw new ErroApi(400, 'pdf_grande', 'O PDF passa de 10 MB.', []);
      return enviarAnexo({
        pessoa_id: pessoa.id, nome_arquivo: arquivo.name, assinado_em: assinadoEm,
        pdf_base64: await lerComoBase64(arquivo),
        ...(matriculaId !== '' ? { matricula_id: matriculaId } : {}),
      });
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['contratos'] }); aoFechar(); },
    onError: (e) => setErro(e as Error),
  });
  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);

  return (
    <Painel aberto aoFechar={aoFechar} titulo="Anexar contrato assinado"
      subtitulo="Contrato assinado fora do CRM. Entra na ficha do aluno já como assinado."
      rodape={(
        <Botao variante="primario" desativado={anexar.isPending} aoClicar={() => anexar.mutate()}>
          {anexar.isPending ? 'Anexando' : 'Anexar'}
        </Botao>
      )}>
      {erro !== null && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}
      {pessoa === null ? (
        <>
          <Busca valor={termo} aoMudar={setTermo} dica="Buscar aluno por nome, telefone ou CPF" />
          {termo.trim().length < 2 && (
            <span className="contratos-opcao__detalhe">Digite ao menos duas letras do nome.</span>
          )}
          {pessoas.isError && <Aviso tom="erro">{mensagemDe(pessoas.error)}</Aviso>}
          {pessoas.isSuccess && pessoas.data.itens.length === 0 && (
            <span className="contratos-opcao__detalhe">Ninguém encontrado.</span>
          )}
          <ul className="contratos-lista">
            {(pessoas.data?.itens ?? []).map((p) => (
              <li key={p.id} className="contratos-opcao">
                <span className="contratos-opcao__texto">
                  <span className="contratos-opcao__nome">{p.nome}</span>
                  <span className="contratos-opcao__detalhe">
                    {[p.telefone_formatado, p.email].filter(Boolean).join(', ')}
                  </span>
                </span>
                <Botao aoClicar={() => setPessoa(p)}>Escolher</Botao>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div className="contratos-opcao contratos-opcao--escolhida">
          <span className="contratos-opcao__texto">
            <span className="contratos-opcao__nome">{pessoa.nome}</span>
            <span className="contratos-opcao__detalhe">{pessoa.email ?? ''}</span>
          </span>
          <Botao variante="fantasma" aoClicar={() => { setPessoa(null); setMatriculaId(''); }}>
            Trocar
          </Botao>
        </div>
      )}

      <Formulario aoEnviar={() => anexar.mutate()}>
        <Campo rotulo="Matrícula" largura="metade" erro={erroDo('matricula_id')}
          dica="Opcional. Com ela, a matrícula conta como contrato assinado.">
          {(id) => (
            <select id={id} value={matriculaId} disabled={pessoa === null}
              onChange={(e) => setMatriculaId(e.target.value)}>
              <option value="">Sem matrícula</option>
              {daPessoa.map((m) => (
                <option key={m.matricula_id} value={m.matricula_id}>
                  {m.curso_nome}, {m.turma_nome}
                </option>
              ))}
            </select>
          )}
        </Campo>
        <Campo rotulo="Data da assinatura" largura="metade" erro={erroDo('assinado_em')}>
          {(id) => (
            <input id={id} type="date" value={assinadoEm} max={hoje()}
              onChange={(e) => setAssinadoEm(e.target.value)} />
          )}
        </Campo>
        <Campo rotulo="PDF assinado" erro={erroDo('pdf_base64')} dica="Arquivo PDF de até 10 MB.">
          {(id) => (
            <input id={id} className="contratos-arquivo" type="file" accept="application/pdf,.pdf"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
          )}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

// Lista

const COLUNAS: readonly Coluna<Contrato>[] = [
  { id: 'aluno', titulo: 'Aluno', celula: (c) => <strong>{c.pessoa_nome}</strong> },
  {
    id: 'curso', titulo: 'Curso', secundaria: true,
    celula: (c) => [c.curso, c.turma].filter(Boolean).join(', '),
  },
  { id: 'valor', titulo: 'Valor', secundaria: true, alinhar: 'direita',
    celula: (c) => c.valor_formatado ?? '' },
  { id: 'status', titulo: 'Status', celula: (c) => <SeloContrato c={c} /> },
  { id: 'quando', titulo: 'Atualizado', secundaria: true, celula: (c) => relativo(c.atualizado_em) },
];

function ListaContratos() {
  const [termo, setTermo] = useState('');
  const [status, setStatus] = useState('');
  const [aberto, setAberto] = useState<string | null>(null);
  // ?id=<uuid> abre o contrato direto (link de tarefa, ficha, Igor)
  const [busca, setBusca] = useSearchParams();
  const idPedido = busca.get('id');
  useEffect(() => { if (idPedido !== null) setAberto(idPedido); }, [idPedido]);
  const [painel, setPainel] = useState<'gerar' | 'anexar' | null>(null);
  const podeGerar = usePermissao('contratos.gerar');
  const podeAnexar = usePermissao('contratos.anexar');
  const q = useQuery({
    queryKey: ['contratos', 'lista', termo, status],
    queryFn: () => ler<{ itens: Contrato[] }>('contratos.listar', { termo, status, limite: 200 }),
    // PDF e envio saem em segundo plano: acompanha ate terminar
    refetchInterval: (consulta) =>
      (consulta.state.data?.itens ?? []).some(andamento) ? 3000 : false,
  });
  const naLista = q.data?.itens.find((c) => c.id === aberto) ?? null;
  // aberto pelo link, mas fora da busca ou do filtro atual
  const avulso = useQuery({
    queryKey: ['contratos', 'um', aberto],
    queryFn: () => ler<{ itens: Contrato[] }>('contratos.status', { id: aberto }),
    enabled: aberto !== null && q.isSuccess && naLista === null,
    refetchInterval: (consulta) =>
      (consulta.state.data?.itens ?? []).some(andamento) ? 3000 : false,
  });
  const contrato = naLista ?? avulso.data?.itens[0] ?? null;
  const fechar = () => {
    setAberto(null);
    if (idPedido !== null) {
      const resto = new URLSearchParams(busca);
      resto.delete('id');
      setBusca(resto, { replace: true });
    }
  };

  return (
    <>
      <Barra acoes={(
        <>
          {podeAnexar && <Botao aoClicar={() => setPainel('anexar')}>Anexar assinado</Botao>}
          {podeGerar && (
            <Botao variante="primario" aoClicar={() => setPainel('gerar')}>Gerar contrato</Botao>
          )}
        </>
      )}>
        <Busca valor={termo} aoMudar={setTermo} dica="Buscar pelo nome do aluno" />
        <select aria-label="Filtrar por status" value={status}
          onChange={(e) => setStatus(e.target.value)}>
          {FILTROS.map((f) => <option key={f.valor} value={f.valor}>{f.rotulo}</option>)}
        </select>
      </Barra>

      {q.isPending && <EstadoCarregando texto="Carregando contratos" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && (
        <EstadoVazio
          titulo={termo !== '' || status !== '' ? 'Nenhum contrato encontrado' : 'Nenhum contrato ainda'}
          texto={termo !== '' || status !== ''
            ? 'Tente outro nome ou outro status.'
            : 'Gere o contrato de uma matrícula ou anexe um assinado fora do CRM.'}
        />
      )}
      {q.isSuccess && q.data.itens.length > 0 && (
        <Tabela colunas={COLUNAS} linhas={q.data.itens} chave={(c) => c.id}
          aoAbrir={(c) => setAberto(c.id)} />
      )}

      {contrato !== null && (
        <PainelContrato key={contrato.id} contrato={contrato} aoFechar={fechar} />
      )}
      {painel === 'gerar' && <PainelGerar aoFechar={() => setPainel(null)} />}
      {painel === 'anexar' && <PainelAnexar aoFechar={() => setPainel(null)} />}
    </>
  );
}

// Modelos

function PainelModelo({ modelo, aoFechar }: { modelo: Modelo | null; aoFechar: () => void }) {
  const qc = useQueryClient();
  const [nome, setNome] = useState(modelo?.nome ?? '');
  const [doc, setDoc] = useState(modelo?.google_doc_url ?? '');
  const [curso, setCurso] = useState(modelo?.curso_codigo ?? '');
  const [ativo, setAtivo] = useState(modelo?.ativo ?? true);
  const [erro, setErro] = useState<Error | null>(null);
  const podeEditar = usePermissao('contratos.editar');
  const salvar = useMutation({
    mutationFn: () => escrever<Modelo>('contratos.modelo_salvar', {
      ...(modelo !== null ? { id: modelo.id } : {}), nome, google_doc_id: doc,
      curso_codigo: curso.trim() === '' ? null : curso, ativo }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['contratos', 'modelos'] });
      aoFechar();
    },
    onError: (e) => setErro(e as Error),
  });
  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);

  return (
    <Painel aberto aoFechar={aoFechar} titulo={modelo === null ? 'Novo modelo' : modelo.nome}
      subtitulo="Google Doc da conta da escola, com os marcadores do contrato."
      rodape={podeEditar ? (
        <Botao variante="primario" desativado={salvar.isPending} aoClicar={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando' : 'Salvar'}
        </Botao>
      ) : undefined}>
      {erro !== null && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}
      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Nome do modelo" erro={erroDo('nome')}>
          {(id) => <input id={id} value={nome} disabled={!podeEditar}
            onChange={(e) => setNome(e.target.value)} placeholder="Contrato padrão" />}
        </Campo>
        <Campo rotulo="Google Doc" erro={erroDo('google_doc_id')}
          dica="Cole o link do documento ou só o id (o trecho entre /d/ e /edit).">
          {(id) => <input id={id} value={doc} disabled={!podeEditar}
            onChange={(e) => setDoc(e.target.value)}
            placeholder="https://docs.google.com/document/d/..." />}
        </Campo>
        <Campo rotulo="Curso" largura="metade" erro={erroDo('curso_codigo')}
          dica="Código do curso, como benini. Vazio vale para todos.">
          {(id) => <input id={id} value={curso} disabled={!podeEditar}
            onChange={(e) => setCurso(e.target.value)} placeholder="Todos os cursos" />}
        </Campo>
        <Campo rotulo="Situação" largura="metade">
          {(id) => (
            <select id={id} value={ativo ? 'ativo' : 'inativo'} disabled={!podeEditar}
              onChange={(e) => setAtivo(e.target.value === 'ativo')}>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          )}
        </Campo>
        <button type="submit" hidden />
      </Formulario>
    </Painel>
  );
}

const COLUNAS_MODELO: readonly Coluna<Modelo>[] = [
  { id: 'nome', titulo: 'Modelo', celula: (m) => <strong>{m.nome}</strong> },
  { id: 'curso', titulo: 'Curso', celula: (m) => m.curso_codigo ?? 'Todos' },
  {
    id: 'doc', titulo: 'Google Doc', secundaria: true,
    celula: (m) => (
      <a href={m.google_doc_url} target="_blank" rel="noreferrer"
        onClick={(e) => e.stopPropagation()}>
        <span className="contratos-marcador">{m.google_doc_id}</span>
      </a>
    ),
  },
  {
    id: 'ativo', titulo: 'Situação',
    celula: (m) => <Selo tom={m.ativo ? 'ok' : 'neutro'}>{m.ativo ? 'Ativo' : 'Inativo'}</Selo>,
  },
];

const COLUNAS_MARCADOR: readonly Coluna<Marcador>[] = [
  { id: 'marcador', titulo: 'Marcador',
    celula: (m) => <span className="contratos-marcador">{m.marcador}</span> },
  { id: 'descricao', titulo: 'Preenche com', celula: (m) => m.descricao },
  {
    id: 'obrigatorio', titulo: 'Uso',
    celula: (m) => <Selo tom={m.obrigatorio ? 'marca' : 'neutro'}>
      {m.obrigatorio ? 'Essencial' : 'Opcional'}</Selo>,
  },
];

function AbaModelos() {
  const [aberto, setAberto] = useState<Modelo | 'novo' | null>(null);
  const podeEditar = usePermissao('contratos.editar');
  const q = useQuery({
    queryKey: ['contratos', 'modelos'],
    queryFn: () => ler<{ itens: Modelo[]; marcadores: Marcador[] }>('contratos.modelos_listar'),
  });

  return (
    <>
      <Barra acoes={podeEditar
        ? <Botao variante="primario" aoClicar={() => setAberto('novo')}>Novo modelo</Botao>
        : undefined} />
      {q.isPending && <EstadoCarregando texto="Carregando modelos" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && (
        <EstadoVazio titulo="Nenhum modelo cadastrado"
          texto="Cadastre o Google Doc do contrato com os marcadores abaixo." />
      )}
      {q.isSuccess && q.data.itens.length > 0 && (
        <Tabela colunas={COLUNAS_MODELO} linhas={q.data.itens} chave={(m) => m.id}
          aoAbrir={(m) => setAberto(m)} />
      )}
      {q.isSuccess && (
        <section className="contratos-marcadores">
          <h3 className="contratos-secao-titulo">Marcadores aceitos</h3>
          <p className="contratos-opcao__detalhe">
            Escreva no Google Doc exatamente como aparece aqui, com as chaves duplas. Na geração,
            cada marcador vira o dado da ficha e da matrícula.
          </p>
          <Tabela colunas={COLUNAS_MARCADOR} linhas={q.data.marcadores} chave={(m) => m.marcador} />
        </section>
      )}
      {aberto !== null && (
        <PainelModelo key={aberto === 'novo' ? 'novo' : aberto.id}
          modelo={aberto === 'novo' ? null : aberto} aoFechar={() => setAberto(null)} />
      )}
    </>
  );
}

// Bloco na ficha do aluno, em Pessoas

/** Os contratos do aluno dentro da ficha dele, com o selo e o PDF. */
function BlocoContratosDoAluno({ cliente }: { cliente: { id: string; nome: string } }) {
  const q = useQuery({
    queryKey: ['contratos', 'da-pessoa', cliente.id],
    queryFn: () => ler<{ itens: Contrato[] }>('contratos.listar',
      { pessoa_id: cliente.id, limite: 20 }),
    refetchInterval: (consulta) =>
      (consulta.state.data?.itens ?? []).some(andamento) ? 3000 : false,
  });
  const itens = q.data?.itens ?? [];
  return (
    <BlocoInicio titulo="Contratos" numero={itens.length}
      tom={itens.some((c) => c.status === 'assinado') ? 'ok' : 'marca'}
      link={{ rotulo: 'Ver em Contratos', href: '/contratos' }}>
      {q.isPending && <span className="ui-item__detalhe">Carregando</span>}
      {q.isError && <span className="ui-item__detalhe">{mensagemDe(q.error)}</span>}
      {q.isSuccess && itens.length === 0 && (
        <span className="ui-item__detalhe">Nenhum contrato deste aluno ainda.</span>
      )}
      {itens.map((c) => (
        <div key={c.id} className="ui-item">
          <div className="ui-item__principal">
            <Link className="ui-item__nome" to={`/contratos?id=${c.id}`}>
              {[c.curso, c.turma].filter(Boolean).join(', ') || 'Contrato'}
            </Link>
            <span className="ui-item__detalhe">
              {c.assinado_em !== null
                ? `Assinado em ${c.origem === 'externo' ? data(c.assinado_em) : dataHora(c.assinado_em)}`
                : `Atualizado ${relativo(c.atualizado_em)}`}
              {c.pdf_assinado_url !== null && (
                <>{', '}<a href={c.pdf_assinado_url} download>PDF assinado</a></>
              )}
              {c.pdf_assinado_url === null && c.pdf_url !== null && (
                <>{', '}<a href={c.pdf_url} download>PDF</a></>
              )}
            </span>
          </div>
          <SeloContrato c={c} />
        </div>
      ))}
    </BlocoInicio>
  );
}

export const tela: TelaDeModulo = {
  slug: 'contratos',
  rota: '/contratos',
  kicker: 'Matrícula',
  titulo: 'Contratos',
  abas: [
    { id: 'contratos', rotulo: 'Contratos', conteudo: ListaContratos },
    { id: 'modelos', rotulo: 'Modelos', conteudo: AbaModelos },
  ],
  blocosDaFicha: [{ id: 'contratos', permissao: 'contratos.ver', conteudo: BlocoContratosDoAluno }],
  dicionario: { pt: {} },
};
